<template>
  <multiselect
    :model-value="selectedOption"
    :options="options"
    label="city_name"
    :track-by="valueField"
    :placeholder="placeholder"
    :searchable="true"
    :internal-search="false"
    :multiple="multiple"
    :close-on-select="!multiple"
    :clear-on-select="false"
    :allow-empty="multiple || allowEmpty"
    :show-labels="false"
    :loading="loading"
    :disabled="disabled"
    :class="{ 'is-invalid': invalid }"
    @update:model-value="onSelect"
    @search-change="onSearch"
    @open="onOpen"
  >
    <template #afterList>
      <li v-if="hasMore" :ref="setSentinel" class="multiselect__element">
        <span class="multiselect__option text-center text-muted">
          Loading more...
        </span>
      </li>
    </template>
  </multiselect>
</template>

<script>
// City dropdown backed by the paginated `/city-paginated` API: the first page
// loads on open, the next page loads as the list is scrolled to its end, and
// typing searches on the server. The model value is the city id.
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import CityService from "@/services/CityService";

export default {
  name: "CitySelect",

  components: { Multiselect },

  props: {
    // Single mode: the city id. Multiple mode: an array of city objects.
    modelValue: { type: [String, Number, Array], default: "" },
    multiple: { type: Boolean, default: false },
    // Which city field is used as the value: "id", or "city_name" for the
    // filters that match on the city name.
    valueField: { type: String, default: "id" },
    placeholder: { type: String, default: "Search and select city" },
    invalid: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    allowEmpty: { type: Boolean, default: false },
    perPage: { type: Number, default: 20 },
  },

  emits: ["update:modelValue", "change"],

  data() {
    return {
      options: [],
      // Saved city that isn't in the loaded pages yet (e.g. edit forms).
      fetchedSelected: null,
      page: 0,
      totalPages: 1,
      search: "",
      loading: false,
      initialized: false,
      requestId: 0,
      searchTimer: null,
      sentinel: null,
      observer: null,
    };
  },

  computed: {
    hasMore() {
      return this.initialized && this.page < this.totalPages;
    },
    hasValue() {
      if (this.multiple) return false;
      return this.modelValue !== "" && this.modelValue !== null && this.modelValue !== undefined;
    },
    selectedOption() {
      if (this.multiple) return Array.isArray(this.modelValue) ? this.modelValue : [];
      if (!this.hasValue) return null;
      const key = this.valueField;
      const value = String(this.modelValue);
      const found =
        this.options.find((el) => String(el[key]) === value) ||
        (this.fetchedSelected && String(this.fetchedSelected[key]) === value
          ? this.fetchedSelected
          : null);
      if (found || key === "id") return found;
      // Value is a plain name that isn't in the loaded pages: show it as is.
      return { [key]: this.modelValue, city_name: this.modelValue };
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.resolveSelected();
      },
    },
  },

  mounted() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      },
      { threshold: 0 }
    );
    if (this.sentinel) this.observer.observe(this.sentinel);
  },

  beforeUnmount() {
    clearTimeout(this.searchTimer);
    this.observer?.disconnect();
  },

  methods: {
    setSentinel(el) {
      if (this.sentinel && this.sentinel !== el) {
        this.observer?.unobserve(this.sentinel);
      }
      this.sentinel = el;
      if (el) this.observer?.observe(el);
    },
    async resolveSelected() {
      if (this.valueField !== "id" || !this.hasValue || this.selectedOption) return;
      try {
        const response = await CityService.getCityById(this.modelValue);
        this.fetchedSelected = response.data;
      } catch (error) {
        console.error("Error fetching city:", error);
      }
    },
    onOpen() {
      if (!this.initialized) this.fetchPage(1);
    },
    onSearch(query) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (query === this.search) return;
        this.search = query;
        this.fetchPage(1);
      }, 300);
    },
    loadMore() {
      if (this.loading || !this.hasMore) return;
      this.fetchPage(this.page + 1);
    },
    async fetchPage(page) {
      const requestId = ++this.requestId;
      this.loading = true;
      try {
        const params = { page, per_page: this.perPage };
        if (this.search) params.search = this.search;
        const response = await CityService.getCitiesPaginated(params);
        if (requestId !== this.requestId) return;
        const { data, pagination } = response.data;
        this.options = page === 1 ? data : [...this.options, ...data];
        this.page = page;
        this.totalPages = pagination?.total_pages || 1;
        this.initialized = true;
      } catch (error) {
        if (requestId === this.requestId) {
          console.error("Error fetching cities:", error);
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
          // Re-arm the observer: it only reports changes, and the sentinel
          // may still be on screen after a short page.
          this.$nextTick(() => {
            if (this.sentinel && this.observer) {
              this.observer.unobserve(this.sentinel);
              this.observer.observe(this.sentinel);
            }
          });
        }
      }
    },
    onSelect(option) {
      if (this.multiple) {
        this.$emit("update:modelValue", option || []);
        this.$emit("change", option || []);
        return;
      }
      this.fetchedSelected = option || null;
      this.$emit("update:modelValue", option ? option[this.valueField] : "");
      this.$emit("change", option || null);
    },
  },
};
</script>
